import React from "react";
import PropTypes from "prop-types";
import classnames from "classnames";

import Link from "shared/components/Link";
import Timestamp from "apps/messenger/components/MainWindow/MessagePane/Timestamp";
import ProfileLinkRequest from "apps/messenger/containers/ProfileLinkRequest";
import SanitizedText from "shared/components/SanitizedText";
import ProfileComment from "shared/components/ProfileComment";
import Str from "shared/components/Str";
import Strf from "shared/components/Strf";
import {
	formatReadReceiptTimeStamp,
	isInstagramReconnectMessage,
	isInstagramReconnectWithPermissionsMessage,
} from "apps/messenger/util/helpers";

import { AUTHED_ROUTES } from "routers/shared/util/consts";
import {
	SYSTEM_USERID,
	ALICE_USERID,
} from "apps/messenger/util/consts";
import ReadReceiptCheckMarks from "shared/components/svgs/read_receipt.svg";
import UnreadIndicatorIcon from "shared/components/svgs/unread_indicator.svg";

import "apps/messenger/components/MainWindow/MessagePane/Message.scss";


const ReadIndicator = ({ formattedTimestamp }) => (
	<div className="messenger-message-double-check-read-indicator">
		<Strf
			className="messenger-message-double-check-read-indicator-text"
			vars={{ formattedTimestamp }}
		>
			Read $formattedTimestamp
		</Strf>
		<ReadReceiptCheckMarks />
	</div>
);

ReadIndicator.propTypes = {
	formattedTimestamp: PropTypes.string.isRequired,
};

const UnreadIndicator = () => (
	<div className="messenger-message-double-check-read-indicator">
		<Str
			className="messenger-message-double-check-read-indicator-text"
		>
			Unread
		</Str>
		<UnreadIndicatorIcon />
	</div>
);

const SentIndicator = () => (
	<div className="messenger-message-sent-indicator">
		<Str
			className="messenger-message-sent-indicator-text"
		>
			Sent
		</Str>
	</div>
);

const InstagramRecconnectMessage = () => {
	const [settingsPage] = AUTHED_ROUTES.Settings;

	return (
		<div className="messenger-message messenger-message--incoming">
			<Strf
				className="messenger-message-bubble"
				vars={{ settingsLink: <Link to={`${settingsPage}/notifsapps?connect_instagram=1`}><Str>Settings page.</Str></Link> }}
			>
				We’ve noticed Instagram signed you out.
				Reconnect your Instagram feed from the $settingsLink
			</Strf>
		</div>
	);
};

const InstagramRecconnectPermissionsMessage = () => {
	const [settingsPage] = AUTHED_ROUTES.Settings;

	return (
		<div className="messenger-message messenger-message--incoming">
			<Strf
				className="messenger-message-bubble"
				vars={{ settingsLink: <Link to={`${settingsPage}/notifsapps?connect_instagram=1`}><Str>Settings page</Str></Link> }}
			>
				You tried to connect your Instagram to your profile,
				but didn’t give us access to your media.
				We can’t display your Instagram photos without this access.
				Reconnect your Instagram feed from the ​$settingsLink and make sure to check the box for
				“Access to your media” when you are prompted.
				You may need to click “Edit Settings”
				and remove OkCupid permissions in the instagram authorization window and reconnect again.
			</Strf>
		</div>
	);
};

const Message = ({
	timestamp,
	from,
	conversationUserId,
	body,
	profileComment,
	profileLinkRequest,
	showTimestamp,
	showReadIndicator,
	readTimestamp,
	showUnreadIndicator,
	showSentIndicator,
	attachments,
}) => {
	// Is it incoming or outgoing?
	const direction = from === conversationUserId
		? "incoming"
		: "outgoing";

	const messageClasses = classnames(
		"messenger-message",
		`messenger-message--${direction}`,
	);

	const isFromAdmin = from === SYSTEM_USERID || from === ALICE_USERID;

	// workaround for the html being stripped from the admin message
	if (isInstagramReconnectMessage(from, body, attachments)) {
		return <InstagramRecconnectMessage />;
	} else if (isInstagramReconnectWithPermissionsMessage(from, attachments)) {
		return <InstagramRecconnectPermissionsMessage />;
	}

	return (
		<>
			<div className={messageClasses}>
				{showTimestamp && <Timestamp timestamp={timestamp} />}
				{profileComment && (
					<ProfileComment className="messenger-message-comment" {...profileComment} />
				)}
				{body && (
					<SanitizedText
						className="messenger-message-bubble"
						text={body}
						shouldDangerouslySetInnerHTML={isFromAdmin}
					/>
				)}
				{showReadIndicator && (
					<ReadIndicator
						formattedTimestamp={formatReadReceiptTimeStamp(readTimestamp)}
					/>
				)}
				{!showReadIndicator && showUnreadIndicator && <UnreadIndicator />}
				{!showReadIndicator && !showUnreadIndicator && showSentIndicator && <SentIndicator />}
			</div>
			{profileLinkRequest && (
				<ProfileLinkRequest {...profileLinkRequest} timestamp={timestamp} />
			)}
		</>
	);
};

Message.propTypes = {
	conversationUserId: PropTypes.string.isRequired,
	from: PropTypes.string.isRequired,

	timestamp: PropTypes.number.isRequired,
	body: PropTypes.string.isRequired,
	profileComment: PropTypes.object,
	profileLinkRequest: PropTypes.object,

	showReadIndicator: PropTypes.bool.isRequired,
	showTimestamp: PropTypes.bool.isRequired,
	readTimestamp: PropTypes.number,
	showUnreadIndicator: PropTypes.bool,
	showSentIndicator: PropTypes.bool,
	attachments: PropTypes.arrayOf(PropTypes.shape({})),
};

Message.defaultProps = {
	profileComment: null,
	profileLinkRequest: null,
	readTimestamp: null,
	showUnreadIndicator: false,
	showSentIndicator: false,
	attachments: [],
};

export default Message;



// WEBPACK FOOTER //
// ./src/apps/messenger/components/MainWindow/MessagePane/Message.jsx